import { useEffect, useState } from 'react';
import { 
  Search, 
  Loader2,
  UserCheck,
  UserX,
  Eye,
  Mail,
  ShoppingBag
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCustomers() {
  const { getAuthHeader } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API}/admin/customers`, { headers: getAuthHeader() });
      setCustomers(res.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Erè pandan chaje kliyan');
    } finally {
      setLoading(false);
    }
  };

  const toggleBlockCustomer = async (customerId, isBlocked) => {
    try {
      const endpoint = isBlocked ? 'unblock' : 'block';
      await axios.put(`${API}/admin/customers/${customerId}/${endpoint}`, {}, {
        headers: getAuthHeader()
      });
      toast.success(isBlocked ? 'Kliyan debloke!' : 'Kliyan bloke!');
      fetchCustomers();
    } catch (error) {
      console.error('Error toggling customer block:', error);
      toast.error('Erè');
    }
  };

  const filteredCustomers = customers.filter(customer => {
    return customer.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           customer.email?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#001A3E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-customers">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#001A3E]">Kliyan</h1>
        <p className="text-gray-500">{customers.length} kliyan total</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-[#001A3E]">{customers.length}</p>
            <p className="text-sm text-gray-500">Total Kliyan</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">
              {customers.filter(c => !c.blocked).length}
            </p>
            <p className="text-sm text-gray-500">Aktif</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-600">
              {customers.filter(c => c.blocked).length}
            </p>
            <p className="text-sm text-gray-500">Bloke</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-purple-600">
              ${customers.reduce((sum, c) => sum + (c.total_spent || 0), 0).toLocaleString()}
            </p>
            <p className="text-sm text-gray-500">Total Depanse</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="search"
              placeholder="Chèche kliyan pa non oswa email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Customers Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600">Kliyan</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600 hidden sm:table-cell">Email</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600 hidden md:table-cell">Kòmann</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600 hidden md:table-cell">Depanse</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600">Status</th>
                  <th className="text-right py-4 px-4 text-sm font-semibold text-gray-600">Aksyon</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredCustomers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#001A3E] flex items-center justify-center text-white font-bold">
                          {customer.name?.charAt(0) || 'U'}
                        </div>
                        <div>
                          <p className="font-medium text-[#001A3E]">{customer.name}</p>
                          <p className="text-xs text-gray-500 sm:hidden">{customer.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-gray-600 hidden sm:table-cell">
                      {customer.email}
                    </td>
                    <td className="py-4 px-4 hidden md:table-cell">
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-sm">
                        {customer.order_count || 0} kòmann
                      </span>
                    </td>
                    <td className="py-4 px-4 hidden md:table-cell">
                      <span className="font-bold text-[#001A3E]">
                        ${(customer.total_spent || 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        customer.blocked
                          ? 'bg-red-100 text-red-700'
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {customer.blocked ? 'Bloke' : 'Aktif'}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedCustomer(customer)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={customer.blocked ? 'text-green-600' : 'text-red-600'}
                          onClick={() => toggleBlockCustomer(customer.id, customer.blocked)}
                        >
                          {customer.blocked ? (
                            <UserCheck className="h-4 w-4" />
                          ) : (
                            <UserX className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredCustomers.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              Pa gen kliyan ki koresponn ak rechèch ou a
            </div>
          )}
        </CardContent>
      </Card>

      {/* Customer Details Dialog */}
      <Dialog open={!!selectedCustomer} onOpenChange={() => setSelectedCustomer(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Detay Kliyan</DialogTitle>
          </DialogHeader>
          
          {selectedCustomer && (
            <div className="space-y-6">
              {/* Customer Info */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-[#001A3E] flex items-center justify-center text-white text-2xl font-bold">
                  {selectedCustomer.name?.charAt(0) || 'U'}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#001A3E]">{selectedCustomer.name}</h3>
                  <p className="text-gray-500 flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    {selectedCustomer.email}
                  </p>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 flex items-center gap-2">
                    <ShoppingBag className="h-4 w-4" />
                    Kòmann
                  </p>
                  <p className="text-2xl font-bold text-[#001A3E]">
                    {selectedCustomer.order_count || 0}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Total Depanse</p>
                  <p className="text-2xl font-bold text-[#001A3E]">
                    ${(selectedCustomer.total_spent || 0).toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p className={`font-medium ${selectedCustomer.blocked ? 'text-red-600' : 'text-green-600'}`}>
                    {selectedCustomer.blocked ? 'Bloke' : 'Aktif'}
                  </p>
                </div>
                <Button
                  variant={selectedCustomer.blocked ? "default" : "destructive"}
                  size="sm"
                  onClick={() => {
                    toggleBlockCustomer(selectedCustomer.id, selectedCustomer.blocked);
                    setSelectedCustomer(null);
                  }}
                >
                  {selectedCustomer.blocked ? 'Debloke' : 'Bloke'}
                </Button>
              </div>

              {/* Created At */}
              <div>
                <p className="text-sm text-gray-500">Enskri</p>
                <p className="font-medium">
                  {new Date(selectedCustomer.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
